<template>
  <div :class="`as-goals-scored ${mode}`">
    <div class="as-goals-scored__head">
      <IconGoalScored :fill="mode === 'as-win' ? '#00FF4B' : '#FF6D6D'" />
      <span> {{ mode == "as-win" ? "Bàn thắng được ghi" : "Bàn thua" }} </span>
    </div>
    <div class="as-goals-scored__body">
      <FootballTeamItem
        :footyStats="footyStats"
        :match="match"
        modifyClass="as-vertical"
        :showName="false"
        :showResult="false"
        :homePoint="homePoint"
        :awayPoint="awayPoint"
      />
      <div class="as-vs">
        <div class="as-vs__tile">
          {{
            mode == "as-win" ? "Ghi được mỗi trận" : "Để thủng lưới mỗi trận"
          }}
        </div>
        <div class="as-vs__line">
          <div class="as-vs-line-item">
            <div class="as-vs-line-item__main"></div>
            <div class="as-vs-line-item__win"></div>
            <div class="as-vs-line-item__draw"></div>
            <div class="as-vs-line-item__loss"></div>
            <div class="as-vs-line-item__ball">
              <div class="as-icon">
                <img
                  v-if="
                    homeTeamLastX.stats.seasonScoredAVG_overall >
                    awayTeamLastX.stats.seasonScoredAVG_overall
                  "
                  :src="getImageUrl(footyStats?.away_image)"
                />
                <img
                  v-else-if="
                    homeTeamLastX.stats.seasonScoredAVG_overall <
                    awayTeamLastX.stats.seasonScoredAVG_overall
                  "
                  :src="getImageUrl(footyStats?.away_image)"
                />
                <IBall v-else />
              </div>
            </div>
          </div>
        </div>
        <div v-if="homePoint > 0 || awayPoint > 0" class="as-vs__text">
          <template v-if="mode === 'as-win'">
            <span class="as-bold">{{ homePoint > awayPoint ? footyStats?.home_name : footyStats?.away_name }}</span> có
            <span class="as-green">{{ getPercentByPoint(homePoint, awayPoint) }}%</span> tốt hơn xét về<br />
            <span class="as-bold">Bàn thắng ghi được</span>
          </template>
          <template v-else>
            <span class="as-bold">{{ homePoint < awayPoint ? footyStats?.home_name : footyStats?.away_name }}</span> có
            <span class="as-green">{{ getPercentByPoint(homePoint, awayPoint) }}%</span> tốt hơn xét về<br />
            <span class="as-bold">Bàn thua</span>
          </template>
        </div>
      </div>
      <FootballTeamItem
        :footyStats="footyStats"
        :match="match"
        modeTeam="as-away"
        modifyClass="as-vertical"
        :showName="false"
        :showResult="false"
        status="as-W"
        :homePoint="homePoint"
        :awayPoint="awayPoint"
      />
    </div>

    <div class="as-history">
      <div class="as-history-item">
        <div class="as-history-item__head">
          <div
            class="as-head-tab"
            :class="{ 'as-active': activeTab === 'all' }"
            @click="setActiveTab('all')"
          >
            Cả trận
          </div>
          <div
            class="as-head-tab"
            :class="{ 'as-active': activeTab === 'half' }"
            @click="setActiveTab('half')"
          >
            Hiệp 1/2
          </div>
        </div>
        <div class="as-history-item__body">
          <div class="as-competition">
            <table class="as-competition__table">
              <thead>
                <tr>
                  <th v-if="mode === 'as-win'">
                    {{
                      activeTab === "all" ? "Đã ghi bàn" : "Scored 1st/2nd Half"
                    }}
                  </th>
                  <th v-else>
                    {{
                      activeTab === "all"
                        ? "Bị thủng lưới"
                        : "Conceded 1st/2nd Half"
                    }}
                  </th>
                  <th class="as-center a">{{ footyStats?.home_name }}</th>
                  <th class="as-center a">{{ footyStats?.away_name }}</th>
                </tr>
              </thead>
              <tbody v-if="activeTab === 'all'">
                <tr v-for="(item, key) in allData" :key="key">
                  <td>{{ item.title }}</td>
                  <td :class="`as-center ${getClassForItem(item.home)}`">
                    {{ item.home }}{{ item.appendTxt }}
                  </td>
                  <td :class="`as-center ${getClassForItem(item.away)}`">
                    {{ item.away }}{{ item.appendTxt }}
                  </td>
                </tr>
              </tbody>
              <tbody v-if="activeTab === 'half'">
                <tr v-for="(item, key) in firstHalfData" :key="key">
                  <td>{{ item.title }}</td>
                  <td :class="`as-center ${getClassForItem(item.home)}`">
                    {{ item.home }}{{ item.appendTxt }}
                  </td>
                  <td :class="`as-center ${getClassForItem(item.away)}`">
                    {{ item.away }}{{ item.appendTxt }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import IconGoalScored from "~src/components/icons/foot_stats/IconGoalScored.vue";
import IBall from "~src/components/icons/incident/IBall.vue";
import FootballTeamItem from "../FootballTeamItem.vue";
import type { IMatch } from "~src/interfaces";
import type {
  IAdditionalInfo,
  IFootyStats,
  ITeam,
  IStatsParent,
} from "~src/interfaces/footystats";
import { computed, ref, onMounted } from "vue";
import { getClassForItem, getImageUrl, getPercentByPoint } from "~src/helpers/footystats";

interface Props {
  match: IMatch;
  mode?: string;
  footyStats: IFootyStats | null;
  homeTeamInfo: ITeam | null;
  awayTeamInfo: ITeam | null;
}

const props = withDefaults(defineProps<Props>(), {
  mode: "as-win",
});

const activeTab = ref("all");
const setActiveTab = (tab: string) => {
  activeTab.value = tab;
};

const homeTeamLastX = computed<IStatsParent>(() => {
  return props.footyStats?.home_team_last_x ?? ({} as IStatsParent);
});

const awayTeamLastX = computed<IStatsParent>(() => {
  return props.footyStats?.away_team_last_x ?? ({} as IStatsParent);
});

const homePoint = computed<number>(() => {
  if (props.mode === "as-win") {
    return homeTeamLastX.value?.stats?.seasonScoredAVG_overall ?? 0;
  }

  return homeTeamLastX.value?.stats?.seasonConcededAVG_overall ?? 0;
});

const awayPoint = computed<number>(() => {
  if (props.mode === "as-win") {
    return awayTeamLastX.value?.stats?.seasonScoredAVG_overall ?? 0;
  }

  return awayTeamLastX.value?.stats?.seasonConcededAVG_overall ?? 0;
});

const allData = computed<any>(() => {
  let home05 =
    homeTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver05Percentage_overall ?? 0;
  let away05 =
    awayTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver05Percentage_overall ?? 0;
  let home15 =
    homeTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver15Percentage_overall ?? 0;
  let away15 =
    awayTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver15Percentage_overall ?? 0;
  let home25 =
    homeTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver25Percentage_overall ?? 0;
  let away25 =
    awayTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver25Percentage_overall ?? 0;
  let home35 =
    homeTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver35Percentage_overall ?? 0;
  let away35 =
    awayTeamLastX.value?.stats?.additional_info
      ?.seasonScoredOver35Percentage_overall ?? 0;
  // let homeFailed = homeTeamLastX.value?.stats?.seasonFTSPercentage_overall ?? 0;
  // let awayFailed = awayTeamLastX.value?.stats?.seasonFTSPercentage_overall ?? 0;

  if (props.mode !== "as-win") {
    home05 =
      homeTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver05Percentage_overall ?? 0;
    away05 =
      awayTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver05Percentage_overall ?? 0;
    home15 =
      homeTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver15Percentage_overall ?? 0;
    away15 =
      awayTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver15Percentage_overall ?? 0;
    home25 =
      homeTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver25Percentage_overall ?? 0;
    away25 =
      awayTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver25Percentage_overall ?? 0;
    home35 =
      homeTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver35Percentage_overall ?? 0;
    away35 =
      awayTeamLastX.value?.stats?.additional_info
        ?.seasonConcededOver35Percentage_overall ?? 0;
    // homeFailed = homeTeamLastX.value?.stats?.seasonFTSPercentage_overall ?? 0;
    // awayFailed = awayTeamLastX.value?.stats?.seasonFTSPercentage_overall ?? 0;
  }

  return [
    {
      title: "Trên 0.5",
      appendTxt: "%",
      home: home05,
      away: away05,
    },
    {
      title: "Trên 1.5",
      appendTxt: "%",
      home: home15,
      away: away15,
    },
    {
      title: "Trên 2.5",
      appendTxt: "%",
      home: home25,
      away: away25,
    },
    {
      title: "Trên 3.5",
      appendTxt: "%",
      home: home35,
      away: away35,
    },
    // {
    //     title: 'Ghi bàn thất bại',
    //     appendTxt: '%',
    //     home: homeFailed,
    //     away: awayFailed
    // },
  ];
});

const firstHalfData = computed<any>(() => {
  // const home1h = 0;
  // const away1h =  0;
  // const home1h = 0;
  // const away1h = 0;
  let homeBothHalves =
    homeTeamLastX.value?.stats?.scoredBothHalvesPercentage_overall ?? 0;
  let awayBothHalves =
    awayTeamLastX.value?.stats?.scoredBothHalvesPercentage_overall ?? 0;
  let homeAverage1H = homeTeamLastX.value?.stats?.scoredAVGHT_overall ?? 0;
  let awayAverage1H = awayTeamLastX.value?.stats?.scoredAVGHT_overall ?? 0;
  let homeAverage2H = homeTeamLastX.value?.stats?.scored_2hg_avg_overall ?? 0;
  let awayAverage2H = awayTeamLastX.value?.stats?.scored_2hg_avg_overall ?? 0;

  if (props.mode !== "as-win") {
    homeAverage1H = homeTeamLastX.value?.stats?.concededAVGHT_overall ?? 0;
    awayAverage1H = awayTeamLastX.value?.stats?.concededAVGHT_overall ?? 0;
    homeAverage2H = homeTeamLastX.value?.stats?.conceded_2hg_avg_overall ?? 0;
    awayAverage2H = awayTeamLastX.value?.stats?.conceded_2hg_avg_overall ?? 0;
  }

  const data = [
    // {
    //     title: props.mode == 'as-win' ? 'Ghi bàn trong H1' : 'Giữ sạch lưới H1',
    //     appendTxt: '%',
    //     home: home1h,
    //     away: away1h
    // },
    // {
    //     title: props.mode == 'as-win' ? 'Ghi bàn trong H2' : 'Giữ sạch lưới H2',
    //     appendTxt: '%',
    //     home: home1h,
    //     away: away1h
    // },
  ];

  if (props.mode === "as-win") {
    data.push({
      title: "Ghi bàn trong cả hai hiệp",
      appendTxt: "%",
      home: homeBothHalves,
      away: awayBothHalves,
    });
  }

  data.push({
    title:
      props.mode == "as-win"
        ? "Điểm trung bình H1"
        : "Trung bình thủng lưới H1",
    appendTxt: "",
    home: homeAverage1H,
    away: awayAverage1H,
  });
  data.push({
    title:
      props.mode == "as-win"
        ? "Điểm trung bình H2"
        : "Trung bình thủng lưới H2",
    appendTxt: "",
    home: homeAverage2H,
    away: awayAverage2H,
  });

  return data;
});
</script>

<script lang="ts">
export default {};
</script>
